import type { InitialArtifactParams, InitialAction } from '@cotera/sdk/core';
import { z } from 'zod';

export type { InitialArtifactParams, InitialAction };

export type PendingChatSubmission = {
  text?: string;
  editorState?: { type: 'doc'; content?: unknown[] };
  files?: Array<Record<string, unknown>>;
  systemSuffix?: string;
  messageType?: 'chat' | 'hidden';
  /** Artifacts to show in the artifact panel when the chat loads (e.g. agent when navigating from agent page) */
  initialArtifacts?: InitialArtifactParams[];
  /** Tool keys to pre-load (e.g. coco/<agentKey> when navigating from agent page) */
  initialToolKeys?: string[];
  /** Actions to apply when the chat is created (e.g. focus_agent when navigating from agent page) */
  initialActions?: InitialAction[];
  /** Model to use for the chat (overrides default) */
  model?: string;
  /** Saved agent resource id; sent as chat context.agentId for DB + ATC KV. */
  agentId?: string;
  /**
   * When true, seed the artifact panel client-side without submitting to the server.
   * Deferred context is applied on the first real user message.
   */
  seedOnly?: boolean;
  /** Client-only chat title shown until the conversation loads a persisted title. */
  displayTitle?: string;
};

type PendingSignupChatSubmission = PendingChatSubmission & {
  conversationId: string;
};

const getPendingChatSubmissionKey = (conversationId: string) =>
  `pending-chat-submission-${conversationId}`;
const SIGNUP_PENDING_CHAT_SUBMISSION_KEY =
  'cotera_signup_pending_chat_submission';

export const setPendingChatSubmission = (
  conversationId: string,
  submission: PendingChatSubmission
): void => {
  sessionStorage.setItem(
    getPendingChatSubmissionKey(conversationId),
    JSON.stringify(submission)
  );
};

function parsePendingChatSubmission(raw: string): PendingChatSubmission | null {
  try {
    const parsed = JSON.parse(raw) as PendingChatSubmission;
    const hasText =
      typeof parsed.text === 'string' && parsed.text.trim().length > 0;
    const hasEditorState =
      parsed.editorState !== undefined &&
      parsed.editorState !== null &&
      typeof parsed.editorState === 'object' &&
      parsed.editorState.type === 'doc';
    const hasFiles = Array.isArray(parsed.files) && parsed.files.length > 0;
    const hasInitialArtifacts =
      Array.isArray(parsed.initialArtifacts) &&
      parsed.initialArtifacts.length > 0;
    const hasInitialToolKeys =
      Array.isArray(parsed.initialToolKeys) &&
      parsed.initialToolKeys.length > 0;
    const initialActions = Array.isArray(parsed.initialActions)
      ? parsed.initialActions
      : [];
    const hasInitialActions = initialActions.length > 0;
    if (
      !hasText &&
      !hasEditorState &&
      !hasFiles &&
      !hasInitialArtifacts &&
      !hasInitialToolKeys &&
      !hasInitialActions
    ) {
      return null;
    }
    if (
      parsed.messageType !== undefined &&
      parsed.messageType !== 'chat' &&
      parsed.messageType !== 'hidden'
    ) {
      return null;
    }
    const initialArtifacts = Array.isArray(parsed.initialArtifacts)
      ? parsed.initialArtifacts.filter(
          (a): a is InitialArtifactParams =>
            typeof a === 'object' &&
            a !== null &&
            typeof (a as { artifactType?: unknown }).artifactType === 'string'
        )
      : undefined;
    const rawAgentId = (parsed as { agentId?: unknown }).agentId;
    const agentIdResult = z.string().uuid().safeParse(rawAgentId);
    const agentId = agentIdResult.success ? agentIdResult.data : undefined;
    const seedOnly = parsed.seedOnly === true;
    return { ...parsed, initialArtifacts, initialActions, agentId, seedOnly };
  } catch {
    return null;
  }
}

/**
 * Read pending submission without removing it. Safe to call during render
 * (no side effects). Use removePendingChatSubmission after the submission
 * has been used.
 */
export const peekPendingChatSubmission = (
  conversationId: string
): PendingChatSubmission | null => {
  const key = getPendingChatSubmissionKey(conversationId);
  const raw = sessionStorage.getItem(key);
  if (raw === null) {
    return null;
  }
  return parsePendingChatSubmission(raw);
};

/**
 * Remove pending submission from storage. Idempotent.
 */
export const removePendingChatSubmission = (conversationId: string): void => {
  sessionStorage.removeItem(getPendingChatSubmissionKey(conversationId));
};

export const consumePendingChatSubmission = (
  conversationId: string
): PendingChatSubmission | null => {
  const key = getPendingChatSubmissionKey(conversationId);
  const raw = sessionStorage.getItem(key);

  if (raw === null) {
    return null;
  }

  sessionStorage.removeItem(key);
  return parsePendingChatSubmission(raw);
};

export const setSignupPendingChatSubmission = (
  submission: PendingSignupChatSubmission
): void => {
  localStorage.setItem(
    SIGNUP_PENDING_CHAT_SUBMISSION_KEY,
    JSON.stringify(submission)
  );
  setPendingChatSubmission(submission.conversationId, {
    text: submission.text,
    editorState: submission.editorState,
    files: submission.files,
    systemSuffix: submission.systemSuffix,
    messageType: submission.messageType,
    initialArtifacts: submission.initialArtifacts,
    initialToolKeys: submission.initialToolKeys,
    initialActions: submission.initialActions,
    ...(submission.agentId !== undefined && { agentId: submission.agentId }),
  });
};

export const consumeSignupPendingChatConversationId = (): string | null => {
  const raw = localStorage.getItem(SIGNUP_PENDING_CHAT_SUBMISSION_KEY);
  if (raw === null) {
    return null;
  }

  localStorage.removeItem(SIGNUP_PENDING_CHAT_SUBMISSION_KEY);

  try {
    const parsed = JSON.parse(raw) as PendingSignupChatSubmission;
    if (
      typeof parsed.conversationId !== 'string' ||
      parsed.conversationId === ''
    ) {
      return null;
    }
    return parsed.conversationId;
  } catch {
    return null;
  }
};
